import { Timestamp } from 'firebase/firestore';
import { boolean, object, string } from 'yup';
import { AppEvent } from '../../lib/types';
import { EVENT_NAME_MAX_LENGTH } from '../../lib/constants';
import { localDateKey } from '../../lib/dates';

// Converts a Firestore Timestamp into the local 'yyyy-mm-dd' string the
// native <input type="date"> expects. Returns '' for missing values so the
// input renders empty rather than NaN.
export const timestampToInputValue = (ts?: Timestamp): string =>
  ts ? localDateKey(ts) : '';

// Human-readable event date range. Returns '' when neither end is set so
// the caller can drop the dates row entirely.
export const formatEventDates = (event: AppEvent): string => {
  const fmt = (ts?: Timestamp) =>
    ts
      ? ts.toDate().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      : '';
  const start = fmt(event.startDate);
  const end = fmt(event.endDate);
  if (start && end) return `${start} – ${end}`;
  if (start) return `from ${start}`;
  if (end) return `until ${end}`;
  return '';
};

export type EventFormValues = {
  name: string;
  startDate: string;
  endDate: string;
  // Mirrors `AppEvent.printGrade`. Stored as a plain boolean in the form
  // even though the doc field is optional — existing events with no
  // value read as `false` (the new default).
  printGrade: boolean;
  // Mirrors `AppEvent.hideCheckout`; absence on the doc reads as `false`.
  hideCheckout: boolean;
  // Mirrors `AppEvent.foldGradeIntoGroupName`; absence reads as `false`.
  foldGradeIntoGroupName: boolean;
  // '' = blank; only used in Add mode for the "copy groups+rotations from"
  // dropdown.
  copyFromRef: string;
};

export const blankEventFormValues: EventFormValues = {
  name: '',
  startDate: '',
  endDate: '',
  printGrade: false,
  hideCheckout: false,
  foldGradeIntoGroupName: false,
  copyFromRef: '',
};

// Yup schema for the event dialog. `existingEvents` (plus optional
// `excludeRef` in edit mode) drives the uniqueness check; endDate
// validates against the in-form startDate via `this.parent`.
export const buildEventSchema = (
  existingEvents: AppEvent[],
  excludeRef?: string,
) =>
  object().shape({
    name: string()
      .trim()
      .required('Name is required')
      .max(
        EVENT_NAME_MAX_LENGTH,
        `Name must be ${EVENT_NAME_MAX_LENGTH} characters or less`,
      )
      .test('unique', 'An event with this name already exists', (v) => {
        if (!v) return true;
        const candidate = v.trim().toLowerCase();
        const conflict = existingEvents.find(
          (e) => e.ref !== excludeRef && e.name.toLowerCase() === candidate,
        );
        return !conflict;
      }),
    startDate: string(),
    endDate: string().test(
      'after-start',
      'End date must be on or after start date',
      function (v) {
        const { startDate } = this.parent as EventFormValues;
        if (!v || !startDate) return true;
        return v >= startDate;
      },
    ),
    printGrade: boolean().required(),
    foldGradeIntoGroupName: boolean().required(),
    copyFromRef: string(),
  });
